// components/oeffentlich/Veranstaltungsuebersicht.jsx — öffentliche
// Übersicht aller Veranstaltungen (22.10.2026, Muster docs/muster-ausschreibung)
//
// Entscheidungen des Inhabers (22.10.2026):
//   - Standard ist die LISTE (nach Monaten, kompakt) mit Logo je Zeile:
//     Turnierlogo, sonst Verbandslogo, sonst TDA-Logo. Karten und Kalender
//     zum Umschalten, die Wahl merkt sich der Browser.
//   - Vergangene Veranstaltungen sind ausgeblendet und zuschaltbar.
// Status und „offen/zu“ kommen vom Server (Feld `anmeldung`).
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Icon, StatusAbzeichen, istVorbei, tag, d2, euro, WT, MON, MON_LANG, logoFuer,
} from './ausschreibungTeile';

const ANSICHT_SCHLUESSEL = 'tda-veranstaltungen-ansicht';
const ANSICHTEN = ['liste', 'karten', 'kalender'];
function ansichtLesen() {
  try {
    const a = localStorage.getItem(ANSICHT_SCHLUESSEL);
    return ANSICHTEN.includes(a) ? a : 'liste';
  } catch { return 'liste'; }
}

const FARBEN = [['#7f1d1d', '#ef4444'], ['#1e3a8a', '#3b82f6'], ['#14532d', '#22c55e'], ['#713f12', '#eab308'], ['#4c1d95', '#a855f7'], ['#134e4a', '#14b8a6'], ['#7c2d12', '#f97316'], ['#1f2937', '#6b7280']];
const verlauf = (id) => { const [a, b] = FARBEN[Math.abs(Number(String(id).replace(/\D/g, '')) || 0) % FARBEN.length]; return `linear-gradient(135deg,${a},${b} 140%)`; };

async function holen(url) {
  const r = await fetch(url);
  const d = await r.json().catch(() => ({}));
  if (!r.ok || d.success === false) throw new Error(d.error || `Fehler ${r.status}`);
  return d.data || [];
}

/** Turnier und Lehrgang auf eine Form bringen. */
function ausTurnier(t) {
  const dis = String(t.kategorien || '').split(',').map((s) => s.trim()).filter(Boolean);
  return {
    key: `t-${t.turnier_id}`, link: `/event/${t.turnier_id}`, art: t.art || 'Turnier', name: t.name,
    von: tag(t.datum_iso), bis: t.end_iso && t.end_iso !== t.datum_iso ? tag(t.end_iso) : null, datumText: t.datum_text,
    ort: t.stadt || t.ort || '', region: t.land || '', veranstalter: t.veranstalter || '',
    dis, anmeldung: t.anmeldung, preis: t.preis_ab, logo: logoFuer(t), bild: t.banner_url || t.plakat_url || null,
  };
}
function ausLehrgang(c) {
  return {
    key: `c-${c.event_id}`, link: `/veranstaltung/${c.event_id}`, art: c.typ || 'Lehrgang', name: c.name,
    von: tag(c.datum_iso), bis: c.end_iso && c.end_iso !== c.datum_iso ? tag(c.end_iso) : null, datumText: null,
    ort: c.ort || '', region: '', veranstalter: c.verein_name || '',
    dis: c.typ ? [c.typ] : [], anmeldung: c.anmeldung, preis: c.preis != null ? Number(c.preis) : null,
    logo: '/logo.webp', bild: c.bild || null,
  };
}

const monatSchluessel = (v) => (v.von ? `${v.von.getFullYear()}-${d2(v.von.getMonth() + 1)}` : 'offen');
const monatText = (k) => (k === 'offen' ? 'Termin folgt' : `${MON_LANG[Number(k.slice(5)) - 1]} ${k.slice(0, 4)}`);
const preisText = (p) => (p == null ? null : p > 0 ? <>ab <b>{euro(p)}</b></> : <b>frei</b>);

const leer = { suche: '', monat: '', dis: '', region: '', art: '', nurOffen: false };

export default function Veranstaltungsuebersicht({ kopfZusatz = null }) {
  const navigate = useNavigate();
  const [alle, setAlle] = useState(null);
  const [fehler, setFehler] = useState('');
  const [ansicht, setAnsicht] = useState(ansichtLesen);
  const [f, setF] = useState(leer);
  const [vorbei, setVorbei] = useState(false);
  const [filterAuf, setFilterAuf] = useState(false);
  const heute = new Date();
  const [kalMonat, setKalMonat] = useState(new Date(heute.getFullYear(), heute.getMonth(), 1));

  useEffect(() => {
    let weg = false;
    Promise.allSettled([holen('/api/turniere/public?vergangene=1'), holen('/api/events/public')]).then(([t, c]) => {
      if (weg) return;
      if (t.status === 'rejected' && c.status === 'rejected') { setFehler('Die Veranstaltungen konnten nicht geladen werden.'); setAlle([]); return; }
      const liste = [
        ...(t.status === 'fulfilled' ? t.value.map(ausTurnier) : []),
        ...(c.status === 'fulfilled' ? c.value.map(ausLehrgang) : []),
      ].sort((a, b) => (a.von ? a.von.getTime() : Infinity) - (b.von ? b.von.getTime() : Infinity));
      setAlle(liste);
      if (t.status === 'rejected' || c.status === 'rejected') setFehler('Ein Teil der Veranstaltungen fehlt, bitte später neu laden.');
    });
    return () => { weg = true; };
  }, []);

  const waehle = (a) => {
    setAnsicht(a);
    try { localStorage.setItem(ANSICHT_SCHLUESSEL, a); } catch { /* ohne Speicher weiter */ }
  };
  const setze = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const sichtbar = useMemo(() => (alle || []).filter((v) => vorbei || !istVorbei(v.anmeldung)), [alle, vorbei]);
  const liste = useMemo(() => {
    const q = f.suche.trim().toLowerCase();
    return sichtbar.filter((v) => {
      if (f.nurOffen && !v.anmeldung?.anmeldbar) return false;
      if (f.monat && monatSchluessel(v) !== f.monat) return false;
      if (f.dis && !v.dis.includes(f.dis)) return false;
      if (f.region && v.region !== f.region) return false;
      if (f.art && v.art !== f.art) return false;
      if (q && !`${v.name} ${v.ort} ${v.veranstalter} ${v.dis.join(' ')}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sichtbar, f]);

  // Auswahllisten aus dem, was da ist; eine Liste mit einem Wert ist keine Wahl
  const monate = [...new Set(sichtbar.map(monatSchluessel))];
  const disziplinen = [...new Set(sichtbar.flatMap((v) => v.dis))].sort((a, b) => a.localeCompare(b, 'de'));
  const regionen = [...new Set(sichtbar.map((v) => v.region).filter(Boolean))];
  const arten = [...new Set(sichtbar.map((v) => v.art))];
  const zahlFilter = (f.monat ? 1 : 0) + (f.dis ? 1 : 0) + (f.region ? 1 : 0) + (f.art ? 1 : 0);
  const aktiv = f.suche || zahlFilter || f.nurOffen;

  const oeffnen = (v) => navigate(v.link);

  let inhalt;
  if (alle === null) inhalt = <div className="au-laden">Veranstaltungen werden geladen …</div>;
  else if (!liste.length) {
    inhalt = (
      <div className="au-leer">
        {sichtbar.length ? 'Keine Veranstaltung passt zu diesen Filtern.' : 'Derzeit ist keine Veranstaltung ausgeschrieben.'}
        {!vorbei && (alle || []).some((v) => istVorbei(v.anmeldung)) && <> <button type="button" className="au-f-zurueck" onClick={() => setVorbei(true)}>Vergangene zeigen</button></>}
      </div>
    );
  } else if (ansicht === 'karten') inhalt = <Karten liste={liste} oeffnen={oeffnen} />;
  else if (ansicht === 'kalender') inhalt = <Kalender liste={liste} oeffnen={oeffnen} monat={kalMonat} setMonat={setKalMonat} heute={heute} />;
  else inhalt = <Liste liste={liste} />;

  const knopf = (k, t, i) => (
    <button type="button" aria-pressed={ansicht === k} onClick={() => waehle(k)}><Icon n={i} />{t}</button>
  );

  return (
    <div className="au-wrap au-breit">
      <div className="au-ue-kopf">
        <div>
          <h1>Veranstaltungen</h1>
          <p>Turniere, Meisterschaften und Lehrgänge. Ein Klick öffnet die Ausschreibung.</p>
        </div>
        <div className="au-ue-mitte">
          <div className="au-ansichten" role="group" aria-label="Ansicht">
            {knopf('liste', 'Liste', 'liste')}{knopf('karten', 'Karten', 'karten')}{knopf('kalender', 'Kalender', 'kal')}
          </div>
          {kopfZusatz}
        </div>
      </div>

      <div className={`au-filter${filterAuf ? ' au-auf' : ''}`} role="search">
        <label className="au-f-suche"><span className="au-sr">Suchen</span><Icon n="such" />
          <input type="search" placeholder="Name, Ort, Verein …" value={f.suche} onChange={(e) => setze('suche', e.target.value)} />
        </label>
        <button type="button" className="au-f-mehr" aria-expanded={filterAuf} onClick={() => setFilterAuf((x) => !x)}>
          Filter{zahlFilter ? ` (${zahlFilter})` : ''}
        </button>
        {monate.length > 1 && (
          <select aria-label="Monat" value={f.monat} onChange={(e) => setze('monat', e.target.value)}>
            <option value="">Alle Monate</option>
            {monate.map((m) => <option key={m} value={m}>{monatText(m)}</option>)}
          </select>
        )}
        {disziplinen.length > 1 && (
          <select aria-label="Disziplin" value={f.dis} onChange={(e) => setze('dis', e.target.value)}>
            <option value="">Alle Disziplinen</option>
            {disziplinen.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        {regionen.length > 1 && (
          <select aria-label="Region" value={f.region} onChange={(e) => setze('region', e.target.value)}>
            <option value="">Alle Regionen</option>
            {regionen.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        {arten.length > 1 && (
          <select aria-label="Art" value={f.art} onChange={(e) => setze('art', e.target.value)}>
            <option value="">Alle Arten</option>
            {arten.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        <label className={`au-schalter${f.nurOffen ? ' au-an' : ''}`}>
          <input type="checkbox" checked={f.nurOffen} onChange={(e) => setze('nurOffen', e.target.checked)} /> Nur Anmeldung offen
        </label>
        <label className={`au-schalter${vorbei ? ' au-an' : ''}`}>
          <input type="checkbox" checked={vorbei} onChange={(e) => setVorbei(e.target.checked)} /> Vergangene
        </label>
        <span className="au-f-anzahl">
          {liste.length} Veranstaltung{liste.length === 1 ? '' : 'en'}
          {aktiv ? <> · <button type="button" className="au-f-zurueck" onClick={() => setF(leer)}>Filter löschen</button></> : null}
        </span>
      </div>
      {fehler && <div className="au-fehler" role="alert">{fehler}</div>}
      {inhalt}
      <div style={{ height: 48 }} />
    </div>
  );
}

function Logo({ v, className }) {
  const [kaputt, setKaputt] = useState(false);
  return (
    <span className={className} aria-hidden="true">
      <img src={kaputt ? '/logo.webp' : v.logo} alt="" loading="lazy" onError={() => setKaputt(true)} />
    </span>
  );
}

function Liste({ liste }) {
  const gruppen = [];
  for (const v of liste) {
    const k = monatSchluessel(v);
    if (!gruppen.length || gruppen[gruppen.length - 1].k !== k) gruppen.push({ k, zeilen: [] });
    gruppen[gruppen.length - 1].zeilen.push(v);
  }
  return gruppen.map((g) => (
    <React.Fragment key={g.k}>
      <div className="au-monat">{monatText(g.k)}</div>
      <div className="au-zeilen">
        {g.zeilen.map((v) => {
          const a = v.anmeldung || {};
          return (
            <Link key={v.key} to={v.link} className={`au-zeile${istVorbei(a) ? ' au-vorbei' : ''}`}>
              <div className="au-dat">
                {v.von ? <><b>{d2(v.von.getDate())}</b><small>{v.bis ? `bis ${d2(v.bis.getDate())}.` : WT[v.von.getDay()]}</small></>
                  : <small>{v.datumText || 'folgt'}</small>}
              </div>
              <Logo v={v} className="au-z-logo" />
              <div className="au-z-name"><strong>{v.name}</strong>
                <div className="au-z-unter"><span className="au-art">{v.art}</span>{v.veranstalter}</div>
              </div>
              <div className="au-z-ort"><Icon n="ort" /><span>{v.ort}</span></div>
              <div className="au-z-dis">
                {v.dis.slice(0, 2).map((d) => <span key={d} className="au-diszi">{d}</span>)}
                {v.dis.length > 2 && <span className="au-mehr" title={v.dis.slice(2).join(', ')}>+{v.dis.length - 2}</span>}
              </div>
              <div className="au-z-st"><StatusAbzeichen anmeldung={a} />{a.zusatz && <small>{a.zusatz}</small>}</div>
              <div className="au-z-preis">{preisText(v.preis)}</div>
              <div className="au-pfeil"><Icon n="pfeil" /></div>
            </Link>
          );
        })}
      </div>
    </React.Fragment>
  ));
}

function Karten({ liste, oeffnen }) {
  return (
    <div className="au-karten">
      {liste.map((v) => {
        const a = v.anmeldung || {};
        return (
          <article key={v.key} className="au-karte" tabIndex={0} role="link" aria-label={v.name}
            onClick={() => oeffnen(v)} onKeyDown={(e) => { if (e.key === 'Enter') oeffnen(v); }}>
            <div className={`au-k-bild${v.bild ? ' au-mit-bild' : ''}`}
              style={{ backgroundImage: v.bild ? `url("${encodeURI(v.bild)}")` : verlauf(v.key) }}>
              {!v.bild && <Logo v={v} className="au-k-logo" />}
              <div className="au-k-dat">{v.von ? <><b>{d2(v.von.getDate())}</b><span>{MON[v.von.getMonth()]} {v.von.getFullYear()}</span></> : <span>{v.datumText || 'Termin folgt'}</span>}</div>
              <StatusAbzeichen anmeldung={a} />
            </div>
            <div className="au-k-text">
              <span className="au-art">{v.art}</span>
              <strong>{v.name}</strong>
              <div className="au-k-zeile"><Icon n="ort" />{v.ort}</div>
              <div className="au-k-zeile" style={{ color: 'var(--au-text-3)' }}>
                {v.dis.slice(0, 3).join(' · ')}{v.dis.length > 3 ? ` +${v.dis.length - 3}` : ''}
              </div>
              <div className="au-k-fuss"><span>{a.zusatz || ' '}</span><span>{preisText(v.preis)}</span></div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

const PUNKT_FARBE = { offen: 'var(--au-gruen)', bald: 'var(--au-gelb)', knapp: 'var(--au-rot)', laeuft: 'var(--au-blau)', erg: 'var(--au-gold)' };

function Kalender({ liste, oeffnen, monat, setMonat, heute }) {
  const j = monat.getFullYear();
  const mo = monat.getMonth();
  const erster = new Date(j, mo, 1);
  const start = new Date(j, mo, 1 - ((erster.getDay() + 6) % 7));
  const zellen = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    if (i >= 28 && i % 7 === 0 && d.getMonth() !== mo) break;
    const evs = liste.filter((v) => v.von && d >= v.von && d <= (v.bis || v.von));
    const istHeute = d.toDateString() === heute.toDateString();
    zellen.push(
      <div key={i} className={`au-kal-tag${d.getMonth() !== mo ? ' au-fremd' : ''}${istHeute ? ' au-heute' : ''}`}>
        <span className="au-n">{d.getDate()}</span>
        {evs.map((v) => (
          <div key={v.key} className="au-kal-ev" style={{ color: PUNKT_FARBE[v.anmeldung?.zustand] || 'var(--au-grau)' }}
            role="link" tabIndex={0} title={v.name} onClick={() => oeffnen(v)} onKeyDown={(e) => { if (e.key === 'Enter') oeffnen(v); }}>
            <span>{v.name}</span><small>{v.ort}</small>
          </div>
        ))}
      </div>,
    );
  }
  const imMonat = liste.filter((v) => v.von && v.von.getMonth() === mo && v.von.getFullYear() === j);
  return (
    <div className="au-kal">
      <div>
        <div className="au-kal-kopf">
          <h2>{MON_LANG[mo]} {j}</h2>
          <button type="button" className="au-kn" aria-label="Voriger Monat" onClick={() => setMonat(new Date(j, mo - 1, 1))}><Icon n="links" /></button>
          <button type="button" className="au-kn" aria-label="Nächster Monat" onClick={() => setMonat(new Date(j, mo + 1, 1))}><Icon n="pfeil" /></button>
        </div>
        <div className="au-kal-gitter">
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((w) => <div key={w} className="au-kal-wt">{w}</div>)}
          {zellen}
        </div>
        <div className="au-legende" style={{ marginTop: 8 }}>
          <span><i style={{ borderColor: 'var(--au-gruen)' }} />Anmeldung offen</span>
          <span><i style={{ borderColor: 'var(--au-gelb)' }} />Meldeschluss bald</span>
          <span><i style={{ borderColor: 'var(--au-blau)' }} />Läuft</span>
          <span><i style={{ borderColor: 'var(--au-gold)' }} />Ergebnisse</span>
          <span><i />Geschlossen oder voll</span>
        </div>
      </div>
      <aside className="au-agenda">
        <h3>{imMonat.length} im {MON_LANG[mo]}</h3>
        {imMonat.length ? imMonat.map((v) => (
          <div key={v.key} className="au-ag" role="link" tabIndex={0} onClick={() => oeffnen(v)} onKeyDown={(e) => { if (e.key === 'Enter') oeffnen(v); }}>
            <div className="au-dat"><b>{d2(v.von.getDate())}</b><small>{WT[v.von.getDay()]}</small></div>
            <div><strong>{v.name}</strong><div>{v.ort}{v.dis.length ? ` · ${v.dis.slice(0, 2).join(', ')}` : ''}</div><StatusAbzeichen anmeldung={v.anmeldung} /></div>
          </div>
        )) : <p style={{ color: 'var(--au-text-3)', fontSize: 13 }}>Keine Veranstaltung in diesem Monat.</p>}
      </aside>
    </div>
  );
}
